/**
 * Shapes the game API returns.
 *
 * These describe the wire format only. Anything that is a *rule* — class stats,
 * appearance options, movement — lives in @emberreach/shared, where the server
 * enforces it too.
 */

export interface ItemDef {
  key: string;
  name: string;
  description: string;
  category: string;
  slot: string | null;
  rarity: string;
  attack: number;
  defence: number;
  heal: number;
  staminaRestore: number;
  stackLimit: number;
  value: number;
  bound: boolean;
  icon: string;
}

export interface BagEntry {
  slotIndex: number;
  item: ItemDef;
  quantity: number;
}

export interface ObjectiveView {
  id: string;
  kind: 'kill' | 'collect' | 'talk' | 'visit';
  description: string;
  targetKey: string;
  required: number;
  counted: number;
  done: boolean;
  zoneId: string;
  position: { x: number; z: number } | null;
  radius: number;
}

export interface QuestView {
  key: string;
  name: string;
  summary: string;
  offerText: string;
  completeText: string;
  giverNpcKey: string;
  turninNpcKey: string;
  zoneId: string;
  minLevel: number;
  rewardExperience: number;
  rewardMarks: number;
  rewardItemKey: string | null;
  rewardItemQuantity: number;
  state: 'available' | 'active' | 'ready' | 'complete' | 'locked';
  objectives: ObjectiveView[];
}

export interface TurnInResult {
  questKey: string;
  experience: number;
  marks: number;
  itemKey: string | null;
  itemQuantity: number;
  level: number;
  levelledUp: boolean;
  completeText: string;
}

export interface NpcView {
  key: string;
  name: string;
  title: string;
  zoneId: string;
  x: number;
  z: number;
  facing: number;
  greeting: string;
  skinTone: string;
  hairColour: string;
  outfit: string;
  shopKey: string | null;
}

export interface ShopListing {
  itemKey: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  rarity: string;
  attack: number;
  defence: number;
  price: number;
  stock: number | null;
}

export interface ShopView {
  key: string;
  name: string;
  buyMultiplier: number;
  sellMultiplier: number;
  listings: ShopListing[];
}

/** Colour per rarity, used by every panel that shows an item. */
export const RARITY_COLOUR: Record<string, string> = {
  common: '#b9a893',
  fine: '#6fa86b',
  rare: '#58a8c4',
  ember: '#ff8a45'
};

// --- advertising -----------------------------------------------------------

/**
 * One booked slot, as the game receives it.
 *
 * There is no destination URL in this type and the server does not send one.
 * The game has no external link to follow, on purpose — an offer is kept while
 * playing and read afterwards, where `SavedOfferView` does carry a link.
 */
export interface ServedAd {
  slotId: string;
  campaignId: string;
  advertiserName: string;
  imageUrl: string;
  kind: 'image' | 'video';
  stillUrl: string;
  offerText: string;
  promoCode: string;
  viewDistanceM: number;
  widthM: number;
  heightM: number;
}

/** A kept offer, read on the offers page after playing. */
export interface SavedOfferView {
  id: string;
  campaignId: string;
  businessName: string;
  offerText: string;
  promoCode: string;
  /** Empty when the link no longer passes the safety check — see linkUnavailable. */
  destinationUrl: string;
  /** True when the advertiser's link was rejected on the way out. */
  linkUnavailable: boolean;
  imageUrl: string;
  zoneId: string;
  savedAt: string;
  openedAt: string | null;
  /** False once the campaign has finished — the offer may no longer stand. */
  stillRunning: boolean;
}

// --- the business console --------------------------------------------------
// Shapes for the advertiser portal and the owner screens. They mirror the
// server's view types rather than its table rows, which is why there are no
// snake_case names and no internal ids in sight.

export interface AdvertiserSummary {
  id: string;
  businessName: string;
  contactName: string;
  contactEmail: string;
  status: string;
  role: string;
}

export interface SlotOffer {
  id: string;
  zoneId: string;
  label: string;
  format: string;
  widthM: number;
  heightM: number;
  viewDistanceM: number;
  showOnMobile: boolean;
  showOnDesktop: boolean;
  acceptedTypes: string[];
  maxFileBytes: number;
  pricingBand: string;
  pricing: { model: string; amountCents: number; description: string }[];
  bookedDays: number;
}

export interface CampaignInput {
  name: string;
  startsOn: string;
  endsOn: string;
  destinationUrl: string;
  offerText: string;
  promoCode: string;
  phone: string;
  targetMobile: boolean;
  targetDesktop: boolean;
  slotIds: string[];
}

export interface CampaignView extends CampaignInput {
  id: string;
  status: string;
  billingModel: string;
  agreedAmountCents: number;
  notes: string;
  artwork: {
    id: string; url: string; mobileUrl: string; stillUrl: string;
    widthPx: number; heightPx: number; status: string; notes: string;
  } | null;
  editable: boolean;
  /** What still has to happen before it can run. The portal shows this list. */
  readiness: { ready: boolean; missing: string[] };
  createdAt: string;
}

export interface CampaignReport {
  campaignId: string;
  name: string;
  status: string;
  serves: number;
  validImpressions: number;
  /** Shown on purpose — an advertiser who can see what was refused can see the
      measurement working. */
  rejectedImpressions: number;
  panelOpens: number;
  offersKept: number;
  offersOpened: number;
  days: {
    day: string; serves: number; impressions: number; rejected: number;
    saves: number; opens: number;
  }[];
}

export interface AdsOverview {
  campaigns: Record<string, number>;
  advertisers: { total: number; active: number };
  awaitingReview: { campaigns: number; artwork: number };
  slots: { total: number; booked: number };
  last30Days: {
    serves: number; validImpressions: number; rejectedImpressions: number;
    panelOpens: number; offersKept: number; offersOpened: number;
  };
  /** Earned by delivery, not money that has arrived. */
  earnedToDateCents: number;
  anomaliesLast7Days: number;
}

export interface AdminCampaignRow {
  id: string;
  name: string;
  status: string;
  advertiserId: string;
  advertiserName: string;
  startsOn: string;
  endsOn: string;
  slotIds: string[];
  billingModel: string;
  agreedAmountCents: number;
  gstCents: number;
  artworkStatus: string | null;
  artworkUrl: string | null;
  offerText: string;
  destinationUrl: string;
  readiness: { ready: boolean; missing: string[] };
  submittedAt: string;
}

export interface PendingAsset {
  id: string;
  campaignId: string;
  campaignName: string;
  advertiserName: string;
  kind: string;
  url: string;
  mobileUrl: string;
  stillUrl: string;
  mimeType: string;
  bytes: number;
  widthPx: number;
  heightPx: number;
  scanResult: string;
  scanNotes: string;
  scannedAt: string | null;
  createdAt: string;
}

export interface AnomalyRow {
  id: string;
  reason: string;
  count: number;
  lastSeen: string;
}

// --- invoices --------------------------------------------------------------

export interface InvoiceLineView {
  id: string;
  campaignId: string | null;
  description: string;
  /** How the amount was worked out, in words. Printed under the description. */
  basis: string;
  billingModel: string;
  quantity: number;
  unitCents: number;
  amountCents: number;
}

export interface InvoiceView {
  id: string;
  number: string;
  advertiserId: string;
  billToName: string;
  billToEmail: string;
  billToAbn: string;
  issuedByName: string;
  issuedByAbn: string;
  issuedByAddress: string;
  issuedByEmail: string;
  paymentTerms: string;
  paymentDetails: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
  /** False when the seller is not registered for GST — see the invoice page. */
  gstApplied: boolean;
  notes: string;
  issuedAt: string | null;
  dueOn: string | null;
  paidAt: string | null;
  voidReason: string;
  createdAt: string;
  lines: InvoiceLineView[];
}

export interface InvoiceSettings {
  businessName: string;
  abn: string;
  address: string;
  email: string;
  phone: string;
  paymentTerms: string;
  paymentDetails: string;
  gstRegistered: boolean;
  nextNumber: number;
}

export interface BillableLine {
  campaignId: string;
  campaignName: string;
  billingModel: string;
  agreedAmountCents: number;
  validImpressions: number;
  interactions: number;
  weeks: number;
  months: number;
  amountCents: number;
  basis: string;
}
